import React, { useEffect } from "react";
import {
  AppAvatar,
  Col,
  Row,
  UiBadge,
  UiButton,
  UiCenter,
  UiDivider,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { formatLongDateTime, getDaysToDate } from "~/utils/helpers/date-time";
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { createBillPortalSession, fetchSubscriptions } from "~/redux/dashboards/thunk";
import { fetchCurrentUserInfo } from "~/redux/users/thunk";
import { PAGES } from "~/constants/app";
import appConfig from "~/config/app-config";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppError } from "ry-error-wrapper";

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
    case "trialing":
      return "green";
    case "canceled":
    case "incomplete":
    case "incomplete_expired":
    case "past_due":
    case "unpaid":
      return "red";
    case "paused":
      return "orange";
    default:
      return "gray";
  }
};

const getStatusDisplay = (status: string) => status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");

const InfoCard: React.FC<{ label: string; value: string; color?: string; badge?: boolean }> = ({
  label,
  value,
  color = "neutral.gray600",
  badge = false,
}) => {
  const vrBody2 = useBreakpointValue<any>({ base: "body3", sm: "body2" });

  return (
    <Col
      p={4}
      bg="neutral.gray00"
      rounded="xl"
      flex="1"
      minW="200px"
      w="full"
      borderWidth="1px"
      borderColor="neutral.gray100"
    >
      <UiText vr="body2" color="neutral.gray500" mb={2}>
        {label}
      </UiText>
      {badge ? (
        <UiBadge colorScheme={color} px={3} py={1} borderRadius="lg" w="fit-content">
          <UiText vr={vrBody2} fontWeight="bold">
            {value}
          </UiText>
        </UiBadge>
      ) : (
        <UiText vr="title2" color={color} fontWeight="600">
          {value}
        </UiText>
      )}
    </Col>
  );
};

const QuotaCard: React.FC<{ label: string; value: string | number; color?: string; highlight?: boolean }> = ({
  label,
  value,
  color = "neutral.gray600",
  highlight = false,
}) => (
  <Col
    p={4}
    bg={highlight ? "lightblue" : "neutral.gray00"}
    rounded="xl"
    flex="1"
    minW="150px"
    borderWidth={highlight ? "2px" : "1px"}
    borderColor={highlight ? "neutral.gray300" : "neutral.gray100"}
  >
    <UiText vr="body3" color="neutral.gray500" mb={2}>
      {label}
    </UiText>
    <UiText vr={highlight ? "headline4" : "title1"} color={color} fontWeight={highlight ? "bold" : "600"}>
      {value}
    </UiText>
  </Col>
);

export const UserProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.currentUser || {});
  const subscriptions = useAppSelector(state => state.dashboard.subscriptions);
  const vrTitle = useBreakpointValue<any>({ base: "headline4", sm: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });
  const orientation = useBreakpointValue<any>({ base: "horizontal", md: "vertical" });
  const { loading } = useRequestState(createBillPortalSession);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchCurrentUserInfo({}));
      await dispatch(fetchSubscriptions({}));
    };
    fetchData();
  }, [dispatch]);

  // Find the latest active subscription
  const getLatestActiveSubscription = () => {
    if (!subscriptions || subscriptions.length === 0) return null;

    const activeSubscriptions = subscriptions.filter(
      subscription => subscription.status === "active" || subscription.status === "trialing"
    );

    if (activeSubscriptions.length === 0) return null;

    // Sort by created_at in descending order to get the latest
    return activeSubscriptions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  };

  const handleCancelSubscription = async () => {
    const postData = {
      success_url: `${appConfig.url}${PAGES.PROFILE}`,
    };
    const resAction = await dispatch(createBillPortalSession({ data: postData }));
    if (requestOK(resAction, createBillPortalSession)) {
      const results = unwrapResult(resAction);
      console.log("results: ", results);
      if (results.portal_url) {
        window.open(results.portal_url, "_self");
      } else {
        throw new AppError("No checkout URL");
      }
    }
  };

  const latestActiveSubscription = getLatestActiveSubscription();

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - User Profile`,
        description: `Manage your account information and quota details`,
        charset: "utf8",
        lang: "en",
        metas: [{ name: "robots", content: "noindex,nofollow" }],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
    >
      <Col flex={1} justify="center">
        <UiCenter p={4}>
          <DashboardMainBox>
            {/* Header */}
            <UiCenter flexDirection="column" mb={8}>
              <AppAvatar
                url={user?.profile_picture}
                w="5rem"
                h="5rem"
                borderRadius="full"
                borderWidth="4px"
                borderColor="white"
                shadow="lg"
              />
              <UiText vr={vrTitle} textAlign="center" mt={4}>
                {user?.name || "User Profile"}
              </UiText>
              <UiText vr={vrCaption} textAlign="center" mt={2} color="neutral.gray600">
                {user?.email || "user@example.com"}
              </UiText>
              {user?.is_all_access_plan && (
                <Row align="center" mt={4}>
                  <UiText textAlign="center" noOfLines={1} vr="title1" fontWeight="bold" className="gradient-text">
                    ALL ACCESS PLAN&nbsp;
                  </UiText>
                  <UiText as="span" fontSize="13px" mb="2px">
                    ⭐
                  </UiText>
                </Row>
              )}
            </UiCenter>

            {/* Main Content - 2 Columns */}
            <Row gap={8} align="flex-start" flexDirection={{ base: "column", lg: "row" }} w="full">
              {/* Left Column - Personal Information */}
              <Col flex="1" w="full">
                <UiText vr="headline4" mb={4} color="darkblue">
                  Personal Information
                </UiText>
                <UiVStack gap={4} w="full">
                  <InfoCard label="Full Name" value={user?.name || "Not provided"} />
                  <InfoCard label="Email Address" value={user?.email || "Not provided"} />
                  <InfoCard
                    label="Plan Status"
                    value={user?.is_all_access_plan ? "All Access" : "Basic"}
                    color={user?.is_all_access_plan ? "green.600" : "neutral.gray600"}
                    badge={!!user?.is_all_access_plan}
                  />
                  <InfoCard
                    label="Member Since"
                    value={user?.created_at ? formatLongDateTime(user.created_at) : "Not available"}
                  />
                  <InfoCard
                    label="Last Updated"
                    value={user?.updated_at ? formatLongDateTime(user.updated_at) : "Not available"}
                  />
                </UiVStack>
              </Col>
              {user?.is_all_access_plan && <UiDivider orientation={orientation} />}
              {/* Right Column - Quota Information */}
              {user?.quota && (
                <Col flex="2" w="full">
                  <UiText vr="headline4" mb={4} color="darkblue">
                    Quota Information
                  </UiText>

                  {/* Quota Stats - 2 columns */}
                  <Row gap={4} mb={4}>
                    <QuotaCard
                      label="Weekly Quota"
                      value={`${user.quota.weekly_quota} styles`}
                      color="darkblue"
                      // highlight
                    />
                    <QuotaCard
                      label="Remaining"
                      value={`${user.quota.remaining_quota} styles`}
                      color={user.quota.remaining_quota > 0 ? "green.600" : "red.500"}
                      highlight={user.quota.remaining_quota > 0}
                    />
                  </Row>

                  <Row gap={4} mb={4}>
                    <QuotaCard label="Photos Per Style" value={user.quota.photos_per_style} />
                    <Col p={4} bg="neutral.gray00" rounded="xl" flex="1" minW="150px">
                      <UiText vr="body3" color="neutral.gray500" mb={2}>
                        Status
                      </UiText>
                      <UiBadge colorScheme={user.quota.is_active ? "green" : "red"} px={3} py={1} borderRadius="lg">
                        <UiText vr="body3" fontWeight="bold">
                          {user.quota.is_active ? "Active" : "Inactive"}
                        </UiText>
                      </UiBadge>
                    </Col>
                  </Row>

                  <UiVStack gap={4} w="full" mb={6}>
                    <InfoCard
                      label="Quota Reset At"
                      value={`${formatLongDateTime(user.quota.quota_reset_at)} (in ${getDaysToDate(
                        user.quota.quota_reset_at
                      )} days)`}
                    />
                    <InfoCard
                      label="Last Used At"
                      value={user.quota.last_used_at ? formatLongDateTime(user.quota.last_used_at) : "Never used"}
                    />
                    <InfoCard label="Quota Created" value={formatLongDateTime(user.quota.created_at)} />
                  </UiVStack>

                  {/* Payment Subscription History */}
                  {subscriptions && subscriptions.length > 0 && (
                    <>
                      <UiText vr="headline5" mb={4} color="darkblue">
                        Subscription History
                      </UiText>
                      <UiVStack gap={4} w="full">
                        {subscriptions.map((subscription, index) => (
                          <Col
                            key={subscription.id}
                            p={4}
                            bg="neutral.gray00"
                            rounded="xl"
                            w="full"
                            borderWidth="1px"
                            borderColor="neutral.gray100"
                          >
                            <Row justify="space-between" align="center" mb={3}>
                              <UiText vr="title1" fontWeight="600">
                                Subscription #{index + 1}
                              </UiText>
                              <UiBadge
                                colorScheme={getStatusColor(subscription.status)}
                                px={3}
                                py={1}
                                borderRadius="lg"
                              >
                                <UiText vr="body3" fontWeight="bold">
                                  {getStatusDisplay(subscription.status)}
                                </UiText>
                              </UiBadge>
                            </Row>

                            <UiVStack gap={3} align="stretch">
                              <Row gap={4}>
                                <Col flex="1">
                                  <UiText vr="body3" color="neutral.gray500" mb={1}>
                                    Period Start
                                  </UiText>
                                  <UiText vr="body2">{formatLongDateTime(subscription.current_period_start)}</UiText>
                                </Col>
                                <Col flex="1">
                                  <UiText vr="body3" color="neutral.gray500" mb={1}>
                                    Period End
                                  </UiText>
                                  <UiText vr="body2">{formatLongDateTime(subscription.current_period_end)}</UiText>
                                </Col>
                              </Row>

                              <Row gap={4}>
                                {subscription.canceled_at && (
                                  <Col flex="1">
                                    <UiText vr="body3" color="neutral.gray500" mb={1}>
                                      Canceled
                                    </UiText>
                                    <UiText vr="body2" color="red.500">
                                      {formatLongDateTime(subscription.canceled_at)}
                                    </UiText>
                                  </Col>
                                )}
                              </Row>

                              {subscription.cancel_at_period_end && (
                                <Col>
                                  <UiBadge colorScheme="orange" px={2} py={1} borderRadius="md">
                                    <UiText vr="body3">Cancels at period end</UiText>
                                  </UiBadge>
                                </Col>
                              )}
                            </UiVStack>
                          </Col>
                        ))}
                      </UiVStack>
                    </>
                  )}
                  {/* Cancel Subscription Button */}
                  {latestActiveSubscription && (
                    <Col align="center">
                      <UiButton
                        border="1px"
                        p={7}
                        mt={8}
                        colorScheme="red"
                        variant="ghost"
                        onClick={handleCancelSubscription}
                        size="sm"
                        fontWeight="normal"
                        isLoading={loading}
                      >
                        Cancel My Subscription
                      </UiButton>
                    </Col>
                  )}
                </Col>
              )}
            </Row>
          </DashboardMainBox>
        </UiCenter>
      </Col>
    </PageLayout>
  );
};
